import {
  IContactActivityResponseItem,
  INote,
  UserTypes,
  accountingSystemSpecificSetting,
  ACCOUNTING_CONFIG,
} from "shared";
import { useRef, useState } from "react";
import { When } from "react-if";
import { Tooltip } from "@mui/material";
import { JSONContent } from "@tiptap/core";
import { PaperClipIcon, XMarkIcon } from "@heroicons/react/24/outline";

import { classNames } from "../../../common/lib/classNames";
import { Paragraph } from "../../../common/Atoms/Typography/Paragraph";
import { useAccountingSystemName } from "../../hooks/useAccountingSystemName";
import { useCurrentOrgUsers } from "../../hooks/useCurrentOrgUsers";
import { useAccountingResources } from "../../hooks/useAccountingResources";
import { useGetSelectedOrganisation } from "../../hooks/useGetSelectedOrganisation";
import { CheckBox } from "../../../common/Atoms/CheckBox";
import { env } from "../../../common/lib/env";

import { Note } from "./Note";
import { ActivityItem } from "./ActivityItem";
import { TextEditor } from "./TextEditor/TextEditor";
import { formatFileSize } from "./NoteAttachments";

export const MAX_NOTE_ATTACHMENTS = 5;

export const MAX_NOTE_ATTACHMENT_BYTES = 10 * 1024 * 1024;

export const NOTE_ATTACHMENT_ACCEPT = `.png,.jpg,.jpeg,.pdf,.txt,.csv`;

interface NotesProps {
  notes: INote[];
  /** When provided, activities are merged into the list by date; notes attached to an activity render inside the activity row */
  activities?: IContactActivityResponseItem[];
  enableNewNote?: boolean;
  onSave: (content: string, mentionedUsers: string[], attachments: File[], syncAttachments: boolean) => Promise<void>;
  /** When provided, shows a cancel button beside the submit button */
  onCancel?: () => void;
  childrenOptions?: React.ReactNode;
  loading?: boolean;
}

export function Notes({ notes, activities, enableNewNote, onSave, onCancel, loading, childrenOptions }: NotesProps) {
  return (
    <div>
      { /* Create a new note */ }
      <When condition={ enableNewNote }>
        <NoteComposer
          onSave={ onSave }
          onCancel={ onCancel }
          loading={ loading }
          childrenOptions={ childrenOptions }
        />
      </When>

      { /* Display existing notes (and note-less activities when provided), newest first.
           Activities with a note render as that note's row via its nested activity. */ }
      <div className={ `w-full space-y-3` }>
        {
          [
            ...notes.map(note => ({
              date: new Date(note.activity?.activityTime || note.createdAt).getTime(),
              node: <Note key={ `note-${note.id}` }
                note={ note } />,
            })),
            ...(activities ?? [])
              .filter(activity => !activity.noteId)
              .map(activity => ({
                date: new Date(activity.activityTime || activity.createdAt).getTime(),
                node: <ActivityItem key={ `activity-${activity.id}` }
                  activity={ activity } />,
              })),
          ]
            .sort((a, b) => b.date - a.date)
            .map(item => item.node)
        }
      </div>
    </div>
  );
}

interface NoteComposerProps {
  onSave: NotesProps[`onSave`];
  onCancel?: () => void;
  childrenOptions?: React.ReactNode;
  loading?: boolean;
  /** Single row editor with inline buttons */
  compact?: boolean;
  /** Compact only: rendered after the submit button on the same row */
  trailing?: React.ReactNode;
  /** Compact only: overrides the submit button label */
  submitLabel?: string;
}

export function NoteComposer({ onSave, onCancel, loading, childrenOptions, compact, trailing, submitLabel }: NoteComposerProps) {
  const accountingName = useAccountingSystemName();
  const organisation = useGetSelectedOrganisation();
  const { data: resources } = useAccountingResources();
  const [newNoteForm, setNewNoteForm] = useState({
    content: ``,
    error: ``,
    jsonContent: null,
  });
  const [attachments, setAttachments] = useState<File[]>([]);
  const [syncAttachments, setSyncAttachments] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canSyncAttachments = !!resources?.attachmentWriteScope;
  const showReconnectHint = attachments.length > 0 && !canSyncAttachments && !!accountingSystemSpecificSetting(organisation, ACCOUNTING_CONFIG.ATTACHMENT_SYNC_NEEDS_RECONNECT);
  const currentOrgUsers = useCurrentOrgUsers(false, [UserTypes.Inherit, UserTypes.Invited]);

  // Recursively find mentions in the JSON content
  function findMentions(json: JSONContent): {label: string; value: string}[] {
    const mentions: {label: string; value: string}[] = [];

    json.content?.forEach(content => {
      if (content.type === `mention`) {
        const user = currentOrgUsers.find(user => user.label === content.attrs.id);

        if (user) {
          mentions.push(user);
        }
      }

      if (content.content) {
        mentions.push(...findMentions(content));
      }
    });

    return mentions;
  }

  function onChange({ text, json }: { text: string; json: JSONContent }) {
    if (text.length > 1500) {
      setNewNoteForm({ content: text, error: `Note must be less than 1500 characters`, jsonContent: json });

      return;
    }

    setNewNoteForm({ error: ``, content: text, jsonContent: json });
  }

  function onPickFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files || []);
    e.target.value = null;

    const next = [...attachments, ...picked];

    if (next.length > MAX_NOTE_ATTACHMENTS) {
      setNewNoteForm(form => ({ ...form, error: `You can attach up to ${MAX_NOTE_ATTACHMENTS} files` }));

      return;
    }

    const tooBig = picked.find(file => file.size > MAX_NOTE_ATTACHMENT_BYTES);

    if (tooBig) {
      setNewNoteForm(form => ({ ...form, error: `${tooBig.name} is larger than 10MB` }));

      return;
    }

    setNewNoteForm(form => ({ ...form, error: `` }));
    setAttachments(next);
  }

  function removeAttachment(index: number) {
    setAttachments(current => current.filter((_, i) => i !== index));
  }

  async function onNewNote(e: React.FormEvent) {
    e.preventDefault();

    const mentions = findMentions(newNoteForm.jsonContent);

    await onSave(newNoteForm.content, mentions.map(mention => mention.value), attachments, canSyncAttachments && syncAttachments);

    // Clear the form
    setNewNoteForm({ content: ``, error: ``, jsonContent: null });
    setAttachments([]);
    setSyncAttachments(false);
  }

  const editor = (
    <TextEditor
      mode={ `note` }
      value={ newNoteForm.jsonContent }
      onChange={ e => onChange(e) }
      mentionValues={ currentOrgUsers.map(user => user.label) }
      placeholder={ `Write a note... use @ to mention someone` }
      editorClassname={ classNames(
        `focus:border-none`,
        // editor.css sets 16px on .editor-class, so size and leading need !important to fit the 2.125rem row
        compact && `!min-h-[2.125rem] !py-1.5 !px-3 rounded-md !text-[0.8125rem] !leading-normal`,
        // Keep the placeholder on one line in the narrow composer: lift it out of flow so it can truncate
        compact && `[&_p.is-editor-empty:first-child]:relative [&_p.is-editor-empty:first-child]:before:absolute [&_p.is-editor-empty:first-child]:before:inset-x-0 [&_p.is-editor-empty:first-child]:before:top-0 [&_p.is-editor-empty:first-child]:before:h-auto [&_p.is-editor-empty:first-child]:before:truncate`,
        newNoteForm.error ? `border border-red-500` : ``,
      ) }
    />
  );

  const attachmentList = (
    <When condition={ attachments.length > 0 }>
      <div className={ classNames(`w-full`, compact ? `mb-2` : `px-2`) }>
        <ul className={ `flex flex-wrap gap-2` }>
          { attachments.map((file, index) => (
            <li
              key={ `${file.name}-${index}` }
              className={ `flex items-center gap-1 border border-gray-200 rounded-md px-2 py-1 text-xs text-gray-600` }
            >
              <PaperClipIcon className={ `h-3 w-3` } />
              <span>{ file.name }</span>
              <span className={ `text-gray-400` }>{ formatFileSize(file.size) }</span>
              <button
                type={ `button` }
                aria-label={ `Remove ${file.name}` }
                onClick={ () => removeAttachment(index) }
                className={ `cursor-pointer hover:text-red-500` }
              >
                <XMarkIcon className={ `h-3 w-3` } />
              </button>
            </li>
          )) }
        </ul>

        <div className={ `mt-3 rounded-md border border-gray-200 bg-gray-50 p-3` }>
          <CheckBox
            name={ `syncAttachments` }
            label={ `Also send attachments to ${accountingName}` }
            description={ canSyncAttachments
              ? `Attached to the ${accountingName} record, not visible to the customer.`
              : `Attachments are saved in Paidnice but will not be sent to ${accountingName}.` }
            checked={ canSyncAttachments && syncAttachments }
            disabled={ !canSyncAttachments }
            onChange={ ({ value }) => setSyncAttachments(value) }
          />

          <When condition={ showReconnectHint }>
            <Paragraph
              variant={ `help` }
              size={ `xs` }
              className={ `mt-2 ml-7` }
            >
              <a
                href={ `${env.apiUrl}/connect/xero` }
                className={ `underline` }
              >
                { `Reconnect to Xero` }
              </a>
              { ` to enable sending attachments to Xero.` }
            </Paragraph>
          </When>
        </div>
      </div>
    </When>
  );

  const buttons = (
    <div className={ classNames(`flex items-center`, compact && `gap-2`) }>
      <input
        type={ `file` }
        ref={ fileInputRef }
        multiple
        accept={ NOTE_ATTACHMENT_ACCEPT }
        onChange={ onPickFiles }
        className={ `hidden` }
      />
      <Tooltip title={ `Attach up to ${MAX_NOTE_ATTACHMENTS} files (png, jpg, pdf, txt, csv), 10MB each` }>
        <button
          type={ `button` }
          aria-label={ `Attach files` }
          onClick={ () => fileInputRef.current?.click() }
          disabled={ loading || attachments.length >= MAX_NOTE_ATTACHMENTS }
          className={ classNames(`cursor-pointer`,
            `border border-gray-200 rounded-md p-2 text-sm text-gray-500 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50`,
            compact ? `flex h-[2.125rem] w-[2.125rem] items-center justify-center p-0` : `my-2`,
          ) }
        >
          <PaperClipIcon className={ `h-4 w-4` } />
        </button>
      </Tooltip>

      <When condition={ !!onCancel }>
        <button
          type={ `button` }
          onClick={ onCancel }
          className={ `cursor-pointer border border-gray-200 rounded-md p-2 my-2 text-sm text-gray-500 hover:bg-gray-100` }
        >
          { `Cancel` }
        </button>
      </When>

      <Tooltip title={ `Notes are also automatically sent to ${accountingName}.` }>
        <span>
          <button
            type={ `submit` }
            className={ compact
              ? `cursor-pointer h-[2.125rem] whitespace-nowrap rounded-md bg-paidniceGreen-600 px-3 text-sm font-medium text-white hover:bg-paidniceGreen-700 disabled:cursor-not-allowed disabled:opacity-50`
              : `border border-gray-200 rounded-md p-2 m-2 text-sm hover:bg-paidniceGreen-500 hover:text-white disabled:cursor-not-allowed disabled:hover:bg-gray-200 disabled:hover:text-gray-600 disabled:bg-gray-200 disabled:opacity-50` }
            disabled={ loading  || !newNoteForm.content || !!newNoteForm.error }
          >
            <When condition={ loading }>
              { `Adding Note...` }
            </When>
            <When condition={ !loading }>
              { compact ? submitLabel || `Add note` : `Add Note` }
            </When>
          </button>
        </span>
      </Tooltip>
    </div>
  );

  const error = (
    <When condition={ newNoteForm.error }>
      <Paragraph variant={ `error` }>
        { newNoteForm.error }
      </Paragraph>
    </When>
  );

  if (compact) {
    return (
      <form onSubmit={ onNewNote }>
        { attachmentList }
        <div className={ `flex items-start gap-2` }>
          <div className={ `min-w-0 flex-1` }>
            { editor }
          </div>
          { buttons }
          { trailing }
        </div>
        { error }
      </form>
    );
  }

  return (
    <form
      className={ `border border-gray-200 rounded-md mb-3` }
      onSubmit={ onNewNote }
    >
      <div className={ `flex flex-col items-end` }>
        <div className={ `w-full` }>
          { editor }
        </div>

        { childrenOptions ? childrenOptions : null }

        { attachmentList }

        { buttons }
      </div>
      { error }
    </form>
  );
}
